// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import {ActiveTicketStatus, MatchEvent} from './events';

/** 获取当前的用户（对接后端 /api/admin/auth/me） */
export async function currentUser(options?: { [key: string]: any }) {
  const res = await request<any>('/api/admin/auth/me', {
    method: 'GET',
    ...(options || {}),
  });
  // 将后端返回的数据映射为 Ant Design Pro 期望的结构
  const u = res?.user || {};
  // 权限码：后端超管返回 ['*']，普通用户返回已授权权限码数组
  const perms: string[] = u.permissions ?? [];
  const isSuper = u.is_super_admin === true;
  const data: API.CurrentUser = {
    name: u.display_name || u.username || '管理员',
    avatar: u.avatar || '',
    userid: String(u.id ?? '0'),
    email: u.email,
    access: isSuper ? 'admin' : (perms[0] ?? ''),
    permissions: perms,
    is_super_admin: isSuper,
  } as API.CurrentUser;
  return { data } as { data: API.CurrentUser };
}

/** 退出登录（对接后端 /api/admin/auth/logout） */
export async function outLogin(options?: { [key: string]: any }) {
  return request<Record<string, any>>('/api/admin/auth/logout', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 登录（对接后端 /api/admin/auth/login） */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  try {
    await request<Record<string, any>>('/api/admin/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: body,
      ...(options || {}),
    });
    // 登录成功，映射为 Pro 期望的登录返回
    const ok: API.LoginResult = {
      status: 'ok',
      type: 'account',
      currentAuthority: 'admin',
    } as any;
    return ok;
  } catch (error) {
    // 登录失败，返回错误状态
    const fail: API.LoginResult = {
      status: 'error',
      type: 'account',
      currentAuthority: 'guest',
    } as any;
    return fail;
  }
}

/** 此处后端没有提供注释 GET /api/notices */
export async function getNotices(options?: { [key: string]: any }) {
  return request<API.NoticeIconList>('/api/notices', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取规则列表 GET /api/rule */
export async function rule(
  params: {
    // query
    /** 当前的页码 */
    current?: number;
    /** 页面的容量 */
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.RuleList>('/api/rule', {
    method: 'GET',
    params: {
      ...params,
    },
    ...(options || {}),
  });
}

/** 更新规则 PUT /api/rule */
export async function updateRule(options?: { [key: string]: any }) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'update',
      ...(options || {}),
    },
  });
}

/** 新建规则 POST /api/rule */
export async function addRule(options?: { [key: string]: any }) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'post',
      ...(options || {}),
    },
  });
}

/** 删除规则 DELETE /api/rule */
export async function removeRule(options?: { [key: string]: any }) {
  return request<Record<string, any>>('/api/rule', {
    method: 'POST',
    data: {
      method: 'delete',
      ...(options || {}),
    },
  });
}

/** Redis查询接口 POST /api/admin/redis/get */
export async function getRedisValue(params: { key: string; redis_instance?: string }, options?: { [key: string]: any }) {
  return request<{
    key: string;
    value: string | null;
    success: boolean;
    message: string;
  }>('/api/admin/redis/get', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** Redis模糊搜索接口 POST /api/admin/redis/search */
export async function searchRedisKeys(params: { pattern: string; limit?: number; redis_instance?: string }, options?: { [key: string]: any }) {
  return request<{
    keys: string[];
    success: boolean;
    message: string;
  }>('/api/admin/redis/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** Redis设置值接口 POST /api/admin/redis/set */
export async function setRedisValue(params: { key: string; value: string; expire?: number; redis_instance?: string }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/redis/set', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** Redis删除Key接口 POST /api/admin/redis/delete */
export async function deleteRedisKey(params: { key: string; redis_instance?: string }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/redis/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

// ========== Redis收藏相关接口 ==========

export type RedisFavoriteGroup = {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type RedisFavoriteKey = {
  id: number;
  group_id: number;
  redis_key: string;
  redis_instance?: string; // Redis实例名称（default, auth, teamup, flo, teamupws），可选以兼容旧数据
  remark?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

/** 获取所有收藏列表 GET /api/admin/redis/favorite-groups */
export async function getFavoriteGroups(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: RedisFavoriteGroup[];
  }>('/api/admin/redis/favorite-groups', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建收藏列表 POST /api/admin/redis/favorite-groups */
export async function createFavoriteGroup(params: { name: string; description?: string; sort_order?: number }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: RedisFavoriteGroup;
  }>('/api/admin/redis/favorite-groups', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 更新收藏列表 PUT /api/admin/redis/favorite-groups */
export async function updateFavoriteGroup(params: { id: number; name?: string; description?: string; sort_order?: number }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/redis/favorite-groups', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 删除收藏列表 DELETE /api/admin/redis/favorite-groups/:id */
export async function deleteFavoriteGroup(id: number, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>(`/api/admin/redis/favorite-groups/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 获取所有收藏的keys GET /api/admin/redis/favorite-keys */
export async function getAllFavoriteKeys(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: RedisFavoriteKey[];
  }>('/api/admin/redis/favorite-keys', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取指定收藏列表的keys GET /api/admin/redis/favorite-keys/group/:group_id */
export async function getFavoriteKeys(groupId: number, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: RedisFavoriteKey[];
  }>(`/api/admin/redis/favorite-keys/group/${groupId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建收藏Key POST /api/admin/redis/favorite-keys */
export async function createFavoriteKey(params: { group_id: number; redis_key: string; redis_instance?: string; remark?: string; sort_order?: number }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: RedisFavoriteKey;
  }>('/api/admin/redis/favorite-keys', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 更新收藏Key PUT /api/admin/redis/favorite-keys */
export async function updateFavoriteKey(params: { id: number; group_id?: number; redis_key?: string; redis_instance?: string; remark?: string; sort_order?: number }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/redis/favorite-keys', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 删除收藏Key DELETE /api/admin/redis/favorite-keys/delete/:id */
export async function deleteFavoriteKey(id: number, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>(`/api/admin/redis/favorite-keys/delete/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** SLS查询接口 POST /api/admin/sls/query */
export async function querySlsLogs(params: {
  project: string;
  logstore: string;
  query: string;
  from_time: number;
  to_time: number;
  lines?: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: {
      logs: Array<Record<string, string>>;
      count: number;
      processed_rows?: number;
      elapsed_millisecond?: number;
      complete: boolean;
    };
  }>('/api/admin/sls/query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 获取SLS项目列表 POST /api/admin/sls/projects */
export async function getSlsProjects(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    projects: Array<{
      name: string;
      description: string;
      status: string;
      region: string;
    }>;
  }>('/api/admin/sls/projects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: {},
    ...(options || {}),
  });
}

/** 获取SLS日志库列表 POST /api/admin/sls/logstores */
export async function getSlsLogstores(params: { project: string }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    logstores: Array<{
      name: string;
      ttl: number;
      shard_count: number;
      create_time: number;
      last_modify_time: number;
    }>;
  }>('/api/admin/sls/logstores', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** SLS连接测试 POST /api/admin/sls/test-connection */
export async function testSlsConnection(params: {
  endpoint: string;
  access_key_id: string;
  access_key_secret: string;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/sls/test-connection', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 获取SLS直方图数据 POST /api/admin/sls/histogram */
export async function getSlsHistogram(params: {
  project: string;
  logstore: string;
  query: string;
  from_time: number;
  to_time: number;
  interval?: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: Array<{
      timestamp: number;
      count: number;
      from_time: number;
      to_time: number;
    }>;
  }>('/api/admin/sls/histogram', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

// ========== SLS查询收藏相关接口 ==========

export type SlsFavoriteGroup = {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type SlsFavoriteQuery = {
  id: number;
  group_id: number;
  name: string;
  project: string;
  logstore: string;
  query: string;
  remark?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

/** 获取所有SLS收藏列表 GET /api/admin/sls/favorite-groups */
export async function getSlsFavoriteGroups(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsFavoriteGroup[];
  }>('/api/admin/sls/favorite-groups', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建SLS收藏列表 POST /api/admin/sls/favorite-groups */
export async function createSlsFavoriteGroup(params: { name: string; description?: string; sort_order?: number }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsFavoriteGroup;
  }>('/api/admin/sls/favorite-groups', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 更新SLS收藏列表 PUT /api/admin/sls/favorite-groups */
export async function updateSlsFavoriteGroup(params: { id: number; name?: string; description?: string; sort_order?: number }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/sls/favorite-groups', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 删除SLS收藏列表 DELETE /api/admin/sls/favorite-groups/:id */
export async function deleteSlsFavoriteGroup(id: number, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>(`/api/admin/sls/favorite-groups/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 获取所有收藏的SLS查询 GET /api/admin/sls/favorite-queries */
export async function getAllSlsFavoriteQueries(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsFavoriteQuery[];
  }>('/api/admin/sls/favorite-queries', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取指定收藏列表的SLS查询 GET /api/admin/sls/favorite-queries/group/:group_id */
export async function getSlsFavoriteQueries(groupId: number, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsFavoriteQuery[];
  }>(`/api/admin/sls/favorite-queries/group/${groupId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建SLS收藏查询 POST /api/admin/sls/favorite-queries */
export async function createSlsFavoriteQuery(params: { 
  group_id: number; 
  name: string; 
  project: string;
  logstore: string;
  query: string;
  remark?: string; 
  sort_order?: number 
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsFavoriteQuery;
  }>('/api/admin/sls/favorite-queries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 更新SLS收藏查询 PUT /api/admin/sls/favorite-queries */
export async function updateSlsFavoriteQuery(params: { 
  id: number; 
  group_id?: number; 
  name?: string; 
  project?: string;
  logstore?: string;
  query?: string;
  remark?: string; 
  sort_order?: number 
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/sls/favorite-queries', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 删除SLS收藏查询 DELETE /api/admin/sls/favorite-queries/delete/:id */
export async function deleteSlsFavoriteQuery(id: number, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>(`/api/admin/sls/favorite-queries/delete/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

// ========== SLS字段配置相关接口 ==========

export type SlsLogstoreFieldConfig = {
  id: number;
  project: string;
  logstore: string;
  display_fields: string[];
  created_at: string;
  updated_at: string;
};

/** 获取指定 project+logstore 的字段配置 POST /api/admin/sls/field-config */
export async function getSlsFieldConfig(params: { project: string; logstore: string }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsLogstoreFieldConfig;
  }>('/api/admin/sls/field-config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 保存/更新字段配置 POST /api/admin/sls/field-config/save */
export async function saveSlsFieldConfig(params: { project: string; logstore: string; display_fields: string[] }, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SlsLogstoreFieldConfig;
  }>('/api/admin/sls/field-config/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

// ==================== 匹配系统调试 API ====================

/** 匹配调试 - 玩家信息 */
export interface DebugPlayer {
  user_id: number;
  nickname: string;
  mmr: number;
}

/** 匹配调试 - 队伍信息 */
export interface DebugTeam {
  team_id: string;
  team_name: string;
  players: DebugPlayer[];
  party_size: number; // 1=单排, 2=双排, 3=3排, 4=4排, 5=5排
  mmr: number; // 队伍平均mmr
}

/** 匹配调试 - 场景信息 */
export interface DebugMatchScenario {
  id: string;
  name: string;
  description: string;
  teams: DebugTeam[];
  /** 快速开始模式：允许更大的MMR差距快速匹配 */
  is_quick?: boolean;
  /** 单排避开车队模式：单排玩家只匹配单排，不匹配双排/3排/4排/5排 */
  is_single_player?: boolean;
  /** 避开最近玩家：key为user_id，value为该玩家需要避开的user_id列表 */
  last_matched_players?: Record<string, string[]>;
}

/** 获取所有匹配调试场景 POST /api/admin/matchmaker/debug/scenarios */
export async function getMatchDebugScenarios(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    scenarios: DebugMatchScenario[];
  }>('/api/admin/matchmaker/debug/scenarios', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: {},
    ...(options || {}),
  });
}

/** 保存匹配调试场景 POST /api/admin/matchmaker/debug/scenarios/save */
export async function saveMatchDebugScenario(scenario: DebugMatchScenario, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/matchmaker/debug/scenarios/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { scenario },
    ...(options || {}),
  });
}

/** 删除匹配调试场景 POST /api/admin/matchmaker/debug/scenarios/delete */
export async function deleteMatchDebugScenario(id: string, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/matchmaker/debug/scenarios/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { id },
    ...(options || {}),
  });
}

/** 将队伍添加到匹配器 POST /api/admin/matchmaker/debug/add-teams */
export async function addTeamsToMatchmaker(
  teams: DebugTeam[], 
  battleType?: string,
  activityGameType?: string,
  scenarioConfig?: { 
    is_quick?: boolean;
    is_single_player?: boolean;
    last_matched_players?: Record<string, string[]>;
  }
) {
  return request<{
    success: boolean;
    message: string;
    queued_teams: string[];
    room_ids: number[];
    ticket_ids: string[]; // om2-matchmaker ticket IDs
  }>('/api/admin/matchmaker/debug/add-teams', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { 
      teams,
      battle_type: battleType,
      activity_game_type: activityGameType,
      ...(scenarioConfig || {}),
    },
  });
}

/** 动态分差控制参数 */
export interface MmrDiffParams {
  /** Time-based adjustment factor (时段调整参数) */
  time_adjustment_factor?: number;
  /** Segment-based adjustment rate (分段调整参数) */
  segment_adjustment_rate?: number;
  /** Downgrade level based on match duration (匹配时长调整参数，0-3) */
  downgrade_level?: number;
  /** Final allowed MMR difference (最终允许匹配分差) */
  allowed_mmr_diff?: number;
}

/** 查询匹配状态 POST /api/admin/matchmaker/debug/query-status */
export async function queryMatchStatus(room_ids: number[], options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    statuses: ActiveTicketStatus[];
  }>('/api/admin/matchmaker/debug/query-status', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { room_ids },
    ...(options || {}),
  });
}

/** 取消匹配 POST /api/admin/matchmaker/debug/cancel-match */
export async function cancelMatch(room_ids: number[], options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    cancelled_count: number;
  }>('/api/admin/matchmaker/debug/cancel-match', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { room_ids },
    ...(options || {}),
  });
}

/** 获取当前活跃的匹配队列 POST /api/admin/matchmaker/debug/active-matches */
export async function getActiveMatches(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    matches: ActiveTicketStatus[];
    by_battle_type: Record<string, ActiveTicketStatus[]>;
  }>('/api/admin/matchmaker/debug/active-matches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: {},
    ...(options || {}),
  });
}

/** 获取匹配历史流水 POST /api/admin/matchmaker/debug/match-history */
export async function getMatchHistory(params: {
  limit?: number;
  battle_type?: string;
  date?: string; // 格式 YYYYMMDD，例如 "20260320"
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    history: MatchEvent[];
  }>('/api/admin/matchmaker/debug/match-history', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 一键取消所有匹配 POST /api/admin/matchmaker/debug/cancel-all-matches */
export async function cancelAllMatches(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    cancelled_count: number;
  }>('/api/admin/matchmaker/debug/cancel-all-matches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: {},
    ...(options || {}),
  });
}

/** 匹配配置相关类型定义 */
export interface DowngradeLevelConfig {
  zero: number;
  one: number;
  two: number;
  three: number;
}

export interface DowngradeDelayConfig {
  zero: number;
  one: number;
  two: number;
  three: number;
}

export interface MmrConfig {
  base_range: number;
  party_size_penalty: number;
  high_mmr_threshold: number;
  high_mmr_range: number;
}

export interface MatchConfig {
  downgrade_levels: DowngradeLevelConfig;
  downgrade_delays: DowngradeDelayConfig;
  mmr_config: MmrConfig;
  save_room_status?: boolean;   // 是否保存房间状态到Redis（用于监控和调试）
  save_task_details?: boolean;  // 是否保存完整任务详情到Redis（用于调试）
}

/** 获取匹配配置 GET /api/admin/match-config/config */
export async function getMatchConfig(params: {
  battle_type: string;
  activity_game_type?: string;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data: MatchConfig;
  }>('/api/admin/match-config/config', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 刷新所有匹配配置缓存 POST /api/admin/match-config/cache/clear-all */
export async function clearAllMatchConfigCache(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/match-config/cache/clear-all', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 获取新版配置 GET /api/admin/om2-config/config */
export async function getOm2Config(params: {
  battle_type: string;
  activity_game_type?: string;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: any;
  }>('/api/admin/om2-config/config', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 更新新版配置 POST /api/admin/om2-config/config/update */
export async function updateOm2Config(params: {
  battle_type: string;
  activity_game_type?: string;
  config: any;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: any;
  }>('/api/admin/om2-config/config/update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 删除新版配置 POST /api/admin/om2-config/config/delete */
export async function deleteOm2Config(params: {
  battle_type: string;
  activity_game_type?: string;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/om2-config/config/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 列出所有新版配置 GET /api/admin/om2-config/list */
export async function listOm2Configs(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    configs: Array<{
      key: string;
      battle_type: string;
      activity_game_type?: string;
    }>;
  }>('/api/admin/om2-config/list', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 清空指定配置缓存 POST /api/admin/om2-config/cache/clear */
export async function clearOm2ConfigCache(params: {
  battle_type: string;
  activity_game_type?: string;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/om2-config/cache/clear', {
    method: 'POST',
    params,
    ...(options || {}),
  });
}

/** 清空所有配置缓存 POST /api/admin/om2-config/cache/clear-all */
export async function clearAllOm2ConfigCache(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/om2-config/cache/clear-all', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 对局结算相关类型定义 */
export interface GameSettlementData {
  game_id: number;
  game_name: string;
  scores: any; // scores 是动态的 JSON 数据
  game_type: string;
  room_type: string;
  match_mode?: string;
  is_invalid: boolean;
  game_time: number;
  start_time: number;
}

/** 查询对局结算信息 GET /api/admin/game-settlement */
export async function getGameSettlement(params: {
  game_id?: number;
  game_name?: string;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: GameSettlementData;
  }>('/api/admin/game-settlement', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 根据昵称搜索用户 GET /api/admin/user/search */
export interface UserInfo {
  user_id: string;
  nick_name?: string;
  user_name?: string;
}

export async function searchUsersByNickname(params: {
  nickname?: string;
  user_id?: string;
  limit?: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data: UserInfo[];
  }>('/api/admin/user/search', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 查询用户对战记录 GET /api/admin/user/match-history */
export interface MatchHistoryItem {
  game_id: number;
  game_name: string;
  start_time?: string;
  end_time?: string;
  created_at?: string; // 创建时间（与 rustwar 逻辑一致）
  status: string;
  game_type: string;
  room_type: string;
  match_type?: string;
  won_team?: number;
  user_team?: number; // 用户所在的队伍
  hero?: string;
  is_invalid: boolean;
}

export async function getUserMatchHistory(params: {
  user_id: string;
  page?: number;
  page_size?: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data: MatchHistoryItem[];
    total: number;
    page: number;
    page_size: number;
  }>('/api/admin/user/match-history', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 踢出用户（下线/踢出游戏） POST /api/admin/user/kickout */
export async function kickoutUser(
  params: { user_id: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/user/kickout', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 调整玩家分数 POST /api/admin/player/score-adjust */
export interface AdjustPlayerScoreParams {
  user_id: string;
  diff_mmr?: number;
  diff_rank_points?: number;
  new_mmr?: number;
  new_rank_points?: number;
  diff_win?: number;
  diff_loss?: number;
  new_win?: number;
  new_loss?: number;
  room_type: string;
  activity_game_type?: string;
  /** 赛季 activity_id（必填） */
  activity_id: number;
  remark?: string;
  finish_grading?: boolean;
  info?: any;
}

export interface AdjustPlayerScoreData {
  activity_id: number;
  activity_record_updated: boolean;
  activity_record_history_updated: boolean;
  leaderboard_updated?: boolean;
  leaderboard_sync_message?: string;
  old_rank_points?: number;
  new_rank_points?: number;
  old_rating?: number;
  new_rating?: number;
  old_win?: number;
  new_win?: number;
  old_loss?: number;
  new_loss?: number;
}

export async function adjustPlayerScore(params: AdjustPlayerScoreParams, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: AdjustPlayerScoreData;
  }>('/api/admin/player/score-adjust', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 手动同步排行榜 POST /api/admin/player/leaderboard-sync */
export interface SyncLeaderboardParams {
  activity_id: number;
  sub_type: string;
  other_type: string;
  user_id: string;
}

export interface SyncLeaderboardData {
  activity_id: number;
  sub_type: string;
  other_type: string;
  user_id: string;
  rank_points?: number;
  score: number;
  leaderboard_updated: boolean;
}

export async function syncLeaderboard(params: SyncLeaderboardParams, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: SyncLeaderboardData;
  }>('/api/admin/player/leaderboard-sync', {
    method: 'POST',
    data: params,
    ...(options || {}),
  });
}

/** 天梯排行榜同步：当前赛季预设 GET /api/admin/player/ladder-sync-preset — roomType 固定 Ladder */
export interface LadderLeaderboardSyncPresetData {
  activity_id: number;
  activity_game_type: string;
  sub_type: string;
  other_type: string;
}

export async function getLadderLeaderboardSyncPreset(
  params: { activity_game_type: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: LadderLeaderboardSyncPresetData;
  }>('/api/admin/player/ladder-sync-preset', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 赛季列表 GET /api/admin/player/season-list?activity_game_type=SuperDota */
export interface SeasonItem {
  activity_id: number;
  activity_name: string;
  activity_game_type: string;
  description: string;
  start_time?: number;
  end_time?: number;
}

export async function getSeasonList(
  params: { activity_game_type: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    seasons: SeasonItem[];
    current_activity_id?: number;
  }>('/api/admin/player/season-list', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 获取用户活动记录 GET /api/admin/player/activity-record */
export interface UserActivityRecordData {
  activity_id: number;
  rank_points?: number;
  rating?: number;
  win?: number;
  loss?: number;
  grading_wins: number;
}

export async function getUserActivityRecord(params: {
  user_id: string;
  room_type: string;
  activity_game_type?: string;
  /** 赛季 activity_id（必填） */
  activity_id: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: UserActivityRecordData;
  }>('/api/admin/player/activity-record', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 查询指定模式（SuperDota/OmgDota/MdDota）top 100 高分玩家 GET /api/admin/player/activity-record-top */
export interface ActivityRecordTopItem {
  rank: number;
  user_id: string;
  rank_points: number;
  mmr: number;
  wins: number;
  losses: number;
  finish_grading: boolean;
  info: any;
}

export interface ActivityRecordTopData {
  activity_id: number;
  activity_game_type: string;
  sub_type: string;
  total_records: number;
  top: ActivityRecordTopItem[];
}

export async function getActivityRecordTop(params: {
  activity_game_type: string;
  /** 赛季 activity_id（必填） */
  activity_id: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: ActivityRecordTopData;
  }>('/api/admin/player/activity-record-top', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 查询分数调整记录 GET /api/admin/player/score-adjust-log */
export interface ScoreAdjustLogItem {
  id: number;
  user_id: string;
  activity_id: number;
  room_type: string;
  activity_game_type?: string;
  diff_mmr: number;
  diff_rank_points: number;
  old_rank_points?: number;
  new_rank_points?: number;
  old_rating?: number;
  new_rating?: number;
  diff_win?: number;
  diff_loss?: number;
  old_win?: number;
  new_win?: number;
  old_loss?: number;
  new_loss?: number;
  activity_record_updated: boolean;
  activity_record_history_updated: boolean;
  operator?: string;
  remark?: string;
  created_at?: string;
}

export async function getScoreAdjustLog(params: {
  user_id?: string;
  page?: number;
  page_size?: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data: ScoreAdjustLogItem[];
    total: number;
    page: number;
    page_size: number;
  }>('/api/admin/player/score-adjust-log', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 查询活动记录扣分记录 GET /api/admin/activity-record/penalty-log */
export interface ActivityRecordAdjustLogItem {
  id: number;
  job_id: number;
  execution_id: string;
  job_execution_date: string;
  biz_type: string;
  user_id: string;
  activity_id: number;
  sub_type: string;
  other_type: string;
  leaderboard_id?: string;
  inactive_days: number;
  last_updated_at?: string;
  diff_mmr: number;
  diff_rank_points: number;
  old_rank_points?: number;
  new_rank_points?: number;
  old_rating?: number;
  new_rating?: number;
  activity_record_updated: boolean;
  activity_record_history_updated: boolean;
  leaderboard_updated: boolean;
  error_message?: string;
  remark?: string;
  created_at?: string;
}

export async function getActivityRecordAdjustLog(params: {
  user_id?: string;
  job_id?: number;
  execution_id?: string;
  biz_type?: string;
  sub_type?: string;
  other_type?: string;
  page?: number;
  page_size?: number;
}, options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data: ActivityRecordAdjustLogItem[];
    total: number;
    page: number;
    page_size: number;
  }>('/api/admin/activity-record/adjust-log', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 获取测试token（对接后端 /api/admin/client/test-token） */
export async function getTestToken(
  body: {
    user_name: string;
    force_refresh?: boolean;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    access_token?: string;
    user_id?: string;
  }>('/api/admin/client/test-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  }).then((res: any) => {
    // 后端返回的是 access_token 和 user_id，需要映射为前端使用的格式
    return {
      accessToken: res.access_token || res.accessToken || '',
      userId: res.user_id || res.userId || '',
    };
  });
}

/** 获取客户端URL（对接后端 /api/admin/client/get-url） */
export async function getClientUrl(
  body: {
    env: 'local' | 'online';
    is_local?: boolean;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    url?: string;
  }>('/api/admin/client/get-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 同步客户端（下载并解压OSS ZIP包） */
export async function syncClient(
  body: {
    env?: 'local' | 'online';
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    storage_path?: string;
  }>('/api/admin/client/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 获取客户端环境配置 */
export async function getClientEnv(
  options?: { [key: string]: any },
) {
  return request<{
    client_env: string;
  }>('/api/admin/client/env', {
    method: 'GET',
    ...(options || {}),
  });
}

// ========== Ticket 生命周期跟踪配置接口 ==========

/** 生命周期跟踪配置类型 */
export interface LifecycleTrackingConfig {
  enabled: boolean;
  sampling_rate: number;
  min_queue_duration_secs: number;
  ttl_secs: number;
}

/** 生命周期统计类型 */
export interface LifecycleStats {
  total_records: number;
  matched_records: number;
  timeout_records: number;
  cancelled_records: number;
  pending_records: number;
  avg_queue_duration_secs: number;
  max_queue_duration_secs: number;
}

/** 清理请求参数 */
export interface LifecycleCleanupRequest {
  max_age_secs: number;
}

/** 获取生命周期跟踪配置 GET /api/admin/lifecycle-tracking/config */
export async function getLifecycleConfig(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    config: LifecycleTrackingConfig;
  }>('/api/admin/lifecycle-tracking/config', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 更新生命周期跟踪配置 POST /api/admin/lifecycle-tracking/config */
export async function updateLifecycleConfig(
  params: LifecycleTrackingConfig,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    config: LifecycleTrackingConfig;
  }>('/api/admin/lifecycle-tracking/config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 重置生命周期跟踪配置为默认值 POST /api/admin/lifecycle-tracking/reset */
export async function resetLifecycleConfig(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    config: LifecycleTrackingConfig;
  }>('/api/admin/lifecycle-tracking/reset', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 获取生命周期统计 GET /api/admin/lifecycle-tracking/stats */
export async function getLifecycleStats(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    stats: LifecycleStats;
    sample_size: number;
    total_keys: number;
  }>('/api/admin/lifecycle-tracking/stats', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 清理过期生命周期记录 POST /api/admin/lifecycle-tracking/cleanup */
export async function cleanupLifecycleRecords(
  params: LifecycleCleanupRequest,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    deleted_count: number;
  }>('/api/admin/lifecycle-tracking/cleanup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 精确查询单个ticket生命周期 */
export async function getTicketLifecycle(params: { room_id: number }, options?: { [key: string]: any }) {
  return request<{ success: boolean; message?: string; ticket?: any }>('/api/admin/lifecycle-tracking/ticket', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: params,
    ...(options || {}),
  });
}

/** 条件筛选tickets */
export async function queryTicketLifecycles(params: any, options?: { [key: string]: any }) {
  return request<{ success: boolean; total_scanned: number; total_matched: number; truncated: boolean; tickets: any[] }>(
    '/api/admin/lifecycle-tracking/query',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: params,
      ...(options || {}),
    }
  );
}

/** 获取未匹配tickets汇总报告 */
export async function getLifecycleSummary(options?: { [key: string]: any }) {
  return request<any>('/api/admin/lifecycle-tracking/summary', {
    method: 'GET',
    ...(options || {}),
  });
}

// ============= 权限申请/审批 =============

/** 提交权限申请 */
export async function createPermissionRequest(
  body: { permission: string; reason?: string },
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; message: string }>('/api/admin/auth/permission-request', {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 我的权限 + 申请记录 */
export async function getMyPermissions(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    data?: { permissions: any[]; requests: any[] };
  }>('/api/admin/auth/my-permissions', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 我的申请列表 */
export async function getMyPermissionRequests(options?: { [key: string]: any }) {
  return request<{ success: boolean; data?: any[] }>(
    '/api/admin/auth/permission-request/mine',
    { method: 'GET', ...(options || {}) },
  );
}

/** 用户列表（需 user_management 权限） */
export async function listAdminUsers(options?: { [key: string]: any }) {
  return request<{ success: boolean; data?: any[] }>('/api/admin/admin/users', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 待审批列表 */
export async function listPermissionRequests(
  params?: { status?: string },
  options?: { [key: string]: any },
) {
  return request<{ success: boolean; data?: any[] }>(
    '/api/admin/admin/permission-requests',
    { method: 'GET', params, ...(options || {}) },
  );
}

/** 审批通过 */
export async function approvePermissionRequest(
  id: number,
  body: { review_note?: string },
  options?: { [key: string]: any },
) {
  return request(`/api/admin/admin/permission-requests/${id}/approve`, {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 审批拒绝 */
export async function rejectPermissionRequest(
  id: number,
  body: { review_note?: string },
  options?: { [key: string]: any },
) {
  return request(`/api/admin/admin/permission-requests/${id}/reject`, {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 直接授予权限 */
export async function grantPermission(
  userId: number,
  body: { permissions: string[]; expires_at?: string },
  options?: { [key: string]: any },
) {
  return request(`/api/admin/admin/users/${userId}/permission`, {
    method: 'POST',
    data: body,
    ...(options || {}),
  });
}

/** 撤销权限 */
export async function revokePermission(userId: number, code: string, options?: { [key: string]: any }) {
  return request(`/api/admin/admin/users/${userId}/permission/${code}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 查询指定用户的已授权权限列表（管理员用） */
export async function listUserPermissions(userId: number, options?: { [key: string]: any }) {
  return request<{ success: boolean; message: string; data?: any[] }>(
    `/api/admin/admin/users/${userId}/permissions`,
    {
      method: 'GET',
      ...(options || {}),
    },
  );
}

/** 查询操作审计日志（仅超管） */
export async function listAuditLogs(
  params: {
    user_id?: number;
    path?: string;
    action?: string;
    status_code?: number;
    start?: string;
    end?: string;
    page?: number;
    page_size?: number;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    data?: any[];
    total: number;
    page: number;
    page_size: number;
  }>('/api/admin/audit/list', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

// ========== 系统维护 - 天梯 Top100 表数据同步 ==========

/** 可同步的天梯排行榜（activities × sky_leaderboards other_type=Human） */
export interface LadderActivityItem {
  activity_id: number;
  activity_name: string;
  /** 活动标题 */
  title: string;
  /** 活动描述——名称多为序号，靠它辨认是哪个赛季 */
  description: string;
  activity_game_type: string;
  sub_type: string;
  other_type: string;
  nakama_leaderboard_id: string;
  start_time: number;
  end_time: number;
  synced_count: number;
  last_synced_at?: string | null;
}

/** 候选玩家（含被过滤项，reason: banned / not_found / duplicated / over_limit） */
export interface LeaderboardSyncCandidate {
  source_rank: number;
  user_id: string;
  /** nakama owner_id（uuid 文本） */
  owner_id: string;
  score: number;
  kept: boolean;
  reason: string;
  final_rank?: number | null;
}

/** 被判定封禁、其 nakama 榜单记录会被删除的玩家 */
export interface LeaderboardSyncBannedPlayer {
  source_rank: number;
  user_id: string;
  owner_id: string;
  score: number;
}

export interface LeaderboardSyncTopItem {
  rank: number;
  user_id: string;
  score: number;
}

export interface LeaderboardSyncResult {
  activity_id: number;
  activity_name: string;
  activity_game_type: string;
  sub_type: string;
  nakama_leaderboard_id: string;
  fetch_limit: number;
  top_n: number;
  fetched: number;
  banned_filtered: number;
  not_found_filtered: number;
  duplicated_filtered: number;
  final_count: number;
  min_score?: number | null;
  max_score?: number | null;
  written: boolean;
  rows_affected: number;
  stale_deleted: number;
  delete_banned_records: boolean;
  banned_players: LeaderboardSyncBannedPlayer[];
  banned_records_deleted: number;
  candidates: LeaderboardSyncCandidate[];
  top: LeaderboardSyncTopItem[];
}

export interface LeaderboardSyncHistoryRow {
  activity_id: number;
  user_id: string;
  rank: number;
  score: number;
  created_at?: string | null;
}

export interface LeaderboardSyncHistorySummaryRow {
  activity_id: number;
  /** 来自 activities，用于辨认赛季与拼 CSV 文件名 */
  activity_game_type: string;
  description: string;
  cnt: number;
  min_score?: number | null;
  max_score?: number | null;
  last_synced_at?: string | null;
}

/** 天梯活动列表（含目标表是否存在、已同步条数） */
export async function getLadderSyncActivities(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: { table_exists: boolean; items: LadderActivityItem[] };
  }>('/api/admin/maintenance/leaderboard-top100/activities', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 预览同步结果（只算不写） */
export async function previewLeaderboardTop100Sync(
  body: {
    activity_id: number;
    sub_type?: string;
    fetch_limit?: number;
    top_n?: number;
    /** 是否删除封禁玩家的 nakama 榜单记录，后端默认 true */
    delete_banned_records?: boolean;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: LeaderboardSyncResult;
  }>('/api/admin/maintenance/leaderboard-top100/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 执行同步（写入 season_leaderboard_history） */
export async function executeLeaderboardTop100Sync(
  body: {
    activity_id: number;
    sub_type?: string;
    fetch_limit?: number;
    top_n?: number;
    /** 是否删除封禁玩家的 nakama 榜单记录，后端默认 true */
    delete_banned_records?: boolean;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: LeaderboardSyncResult;
  }>('/api/admin/maintenance/leaderboard-top100/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 初始化目标表（CREATE TABLE IF NOT EXISTS，幂等） */
export async function initLeaderboardTop100Table(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: { table_exists: boolean; created: boolean };
  }>('/api/admin/maintenance/leaderboard-top100/init-table', {
    method: 'POST',
    ...(options || {}),
  });
}

/** 导出某赛季已归档名次为 CSV（后端直接返回 text/csv） */
export async function exportLeaderboardTop100Csv(
  params: { activity_id: number },
  options?: { [key: string]: any },
) {
  return request<Blob>('/api/admin/maintenance/leaderboard-top100/export', {
    method: 'GET',
    params,
    responseType: 'blob',
    ...(options || {}),
  });
}

/** 查询已同步数据（不传 activity_id 时只返回各赛季汇总） */
export async function getLeaderboardTop100History(
  params: { activity_id?: number },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: {
      table_exists: boolean;
      summary: LeaderboardSyncHistorySummaryRow[];
      rows: LeaderboardSyncHistoryRow[];
    };
  }>('/api/admin/maintenance/leaderboard-top100/history', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

// ========== 系统维护 - 停服/开服 与 FLO 对局清理 ==========

/** 高危操作确认码（服务端逐字校验，不匹配直接 400） */
export const SERVICE_OPS_CONFIRM = {
  disable: 'DISABLE-ACCESS',
  enable: 'ENABLE-ACCESS',
  cleanup: 'CLEANUP-GAMES',
  cleanupRoom: 'CLEANUP-ROOM',
  schedule: 'SCHEDULE-MAINTENANCE',
  cancelSchedule: 'CANCEL-MAINTENANCE',
  extendSchedule: 'EXTEND-MAINTENANCE',
} as const;

/** admin-api 实际连接的环境标识 */
export interface ServiceEnvInfo {
  /** PROD / DEV / LOCAL / UNKNOWN */
  label: string;
  /** 判定依据：namespace / db_user / unknown */
  label_source: string;
  namespace?: string | null;
  /** 各数据源 host:port/db（已剥离凭据） */
  bbc?: string | null;
  flo?: string | null;
  auth_redis?: string | null;
}

export interface ServiceAccessStatus {
  /** tz_sys_config.param_value 原始值，行不存在为 null */
  db_value?: string | null;
  /** db_value === '1' 即停服中 */
  disabled: boolean;
  redis_sys_config_object?: string | null;
  redis_sys_config?: string | null;
  /** 两个 Java 缓存键是否都已失效 */
  redis_cache_cleared: boolean;
  /** 当前后端连的是哪套环境 */
  env: ServiceEnvInfo;
}

export interface CleanupGamesResult {
  dry_run: boolean;
  max_game_id: number;
  threshold_id: number;
  /** dry_run 时为预计影响行数，执行时为实际影响行数 */
  games_updated: number;
  slots_deleted: number;
}

/** 查询停服状态（DB + 两个 Redis 缓存键） */
export async function getServiceAccessStatus(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: ServiceAccessStatus;
  }>('/api/admin/maintenance/service/access-status', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 停服：ACCESS_DISABLE=1 并失效缓存，需确认码 */
export async function disableServiceAccess(
  body: { confirm: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: ServiceAccessStatus;
  }>('/api/admin/maintenance/service/disable-access', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 开服：ACCESS_DISABLE=0 并失效缓存，需确认码 */
export async function enableServiceAccess(
  body: { confirm: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: ServiceAccessStatus;
  }>('/api/admin/maintenance/service/enable-access', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** FLO 残留对局清理；dry_run 缺省为 true，真正执行需确认码 */
export async function cleanupFloGames(
  body: { dry_run?: boolean; confirm?: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: CleanupGamesResult;
  }>('/api/admin/maintenance/service/cleanup-games', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 房间列表清理（teamup Redis room:basic:*） */
export interface RoomListItem {
  room_id: number;
  room_name: string;
  room_type: string;
  game_type: string;
  join_count: number;
  max_join_count: number;
  room_state: string;
  created_by_nick?: string;
  created_by_user_id?: string;
  /** epoch 秒 */
  created_at?: number;
  /** room:basic 剩余 TTL（秒）；-1 表示不过期 */
  ttl_seconds: number;
}

export interface RoomsResult {
  total: number;
  rooms: RoomListItem[];
}

export interface CleanupRoomResult {
  room_id: number;
  deleted_keys: string[];
}

/** 预览 teamup Redis 中的房间（停服后残留的自建房） */
export async function getRooms(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: RoomsResult;
  }>('/api/admin/maintenance/service/rooms', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 清理单个房间；需确认码 */
export async function cleanupRoom(
  body: { room_id: number; confirm?: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: CleanupRoomResult;
  }>('/api/admin/maintenance/service/rooms/cleanup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 单个 flo-node 上的在线玩家数 */
export interface OnlineNode {
  pod: string;
  /** flo-node / flo-node-standby */
  job: string;
  players: number;
}

/** 按匹配类型分的 10 人对局数 */
export interface OnlineGameGroup {
  match_type: string;
  games: number;
}

export interface OnlineTrendPoint {
  /** 秒级时间戳 */
  ts: number;
  players: number;
}

/**
 * 实时在线玩家数。
 * 数据源为集群内 Prometheus，PromQL 与 Grafana「FloNode Game Server Overview」逐字一致：
 *   total  = sum(flonode_player_connections)
 *   nodes  = flonode_player_connections            （legend {{pod}}）
 *   games  = sum by(match_type) (flonode_game_sessions{player_count="10"})
 */
export interface OnlinePlayers {
  total_players: number;
  node_count: number;
  nodes: OnlineNode[];
  total_games: number;
  games_by_match_type: OnlineGameGroup[];
  trend: OnlineTrendPoint[];
  range_minutes: number;
  /** 数据截至时间（秒级时间戳） */
  queried_at: number;
  /** Prometheus 地址（已剥离 scheme） */
  prometheus?: string | null;
}

/** 查询实时在线玩家数；range_minutes 控制趋势窗口，服务端夹紧到 [5, 360] */
export async function getOnlinePlayers(
  params?: { range_minutes?: number },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: OnlinePlayers;
  }>('/api/admin/maintenance/service/online-players', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

// ========== 系统维护 - 定时维护（自动停服/开服）==========

/**
 * pending 待停服 / active 停服中 / opening 正在自动开服 /
 * completed 已开服 / cancelled 已取消 / missed 错过窗口未执行
 *
 * opening 是抢到「执行自动开服」所有权后的短暂中间态，用于和延长停服互斥，
 * 正常只会存在几百毫秒。
 */
export type MaintenanceScheduleStatus =
  | 'pending'
  | 'active'
  | 'opening'
  | 'completed'
  | 'cancelled'
  | 'missed';

/**
 * 一条定时维护计划。
 * 所有时间都是 UTC 毫秒时间戳，展示时按固定 +08:00 换算成北京时间
 * （中国自 1991 年起无夏令时，不需要 tzdata）。
 */
export interface MaintenanceSchedule {
  id: number;
  /** 计划停服时刻 */
  start_at_ms: number;
  duration_min: number;
  /** 计划开服时刻 */
  end_at_ms: number;
  status: MaintenanceScheduleStatus;
  note: string;
  created_by: string;
  cancelled_by?: string | null;
  /** 实际停服时刻 */
  disabled_at_ms?: number | null;
  /** 实际开服时刻 */
  enabled_at_ms?: number | null;
  /** 最近一次自动执行失败原因；调度会持续重试 */
  last_error?: string | null;
}

export interface MaintenanceScheduleView {
  /** 当前未结束的计划（pending 或 active），没有则为 null */
  current?: MaintenanceSchedule | null;
  history: MaintenanceSchedule[];
  /** 服务端当前时刻，用于对齐倒计时，避免受本机时钟偏差影响 */
  server_now_ms: number;
}

/** 查询定时维护计划与历史 */
export async function getMaintenanceSchedule(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: MaintenanceScheduleView;
  }>('/api/admin/maintenance/service/schedule', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 创建定时维护计划：到 start_at_ms 自动停服，duration_min 到期自动开服 */
export async function createMaintenanceSchedule(
  body: {
    /** UTC 毫秒时间戳（把北京时间墙上时钟按 +08:00 换算后的绝对值） */
    start_at_ms: number;
    duration_min: number;
    note?: string;
    confirm: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: MaintenanceScheduleView;
  }>('/api/admin/maintenance/service/schedule/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 取消计划；若计划已在停服中(active)，后端会先自动开服再结束该计划 */
export async function cancelMaintenanceSchedule(
  body: { id: number; confirm: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: MaintenanceScheduleView;
  }>('/api/admin/maintenance/service/schedule/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/**
 * 延长停服时间：把计划的开服时刻往后推，自动开服相应顺延。
 * 只有停服中(active)的计划能延长；若调度器已开始执行自动开服，接口返回冲突。
 */
export async function extendMaintenanceSchedule(
  body: { id: number; extend_min: number; confirm: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: MaintenanceScheduleView;
  }>('/api/admin/maintenance/service/schedule/extend', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

// ========== 系统维护 - 联赛预选赛资格 ==========

export interface LeagueQualifiedUser {
  activity_id: number;
  user_id: string;
  best_score: number;
  qualified_at?: string | null;
  updated_at?: string | null;
}

/** 手动触发的后台任务状态 */
export interface LeagueQualifyRunState {
  running: boolean;
  started_at?: string | null;
  finished_at?: string | null;
  operator?: string | null;
  last_stats?: LeagueQualifySyncStats | null;
  last_error?: string | null;
}

export interface LeagueQualifyStatus {
  activity_id: number;
  /** 是否为自动解析出的当前赛季 */
  is_current_season: boolean;
  game_type: string;
  sub_type: string;
  other_type: string;
  /** 资格线（后端常量） */
  threshold: number;
  total_qualified: number;
  users: LeagueQualifiedUser[];
  /** 后台同步任务状态，前端据此轮询 */
  run_state: LeagueQualifyRunState;
}

export interface LeagueQualifySyncStats {
  activity_id: number;
  from_record_id: number;
  to_record_id: number;
  chunks: number;
  matched_rows: number;
  rows_affected: number;
  total_qualified: number;
  full_backfill: boolean;
}

/** 资格状态与达标名单 */
export async function getLeagueQualifyStatus(
  params: { activity_id?: number; limit?: number; offset?: number },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: LeagueQualifyStatus;
  }>('/api/admin/maintenance/league-qualify/status', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 查某个玩家是否已达标 */
export async function checkLeagueQualifyUser(
  params: { user_id: string; activity_id?: number },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: {
      activity_id: number;
      user_id: string;
      qualified: boolean;
      record?: LeagueQualifiedUser | null;
    };
  }>('/api/admin/maintenance/league-qualify/check', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 手动触发同步（与 XXL-JOB 同一入口，幂等）。立即返回，同步在后台跑，进度靠 status 轮询 */
export async function runLeagueQualifySync(
  body: { activity_id?: number },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: LeagueQualifyRunState;
  }>('/api/admin/maintenance/league-qualify/run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

// ============================================================
// 系统维护 - 数据库清理（对局数据）
// ============================================================

/** 执行清理的确认码，需与后端 db_cleanup.rs 的 CONFIRM_CODE 一致 */
export const DB_CLEANUP_CONFIRM = 'DELETE_MATCH_HISTORY';

export type DbCleanupTableStat = {
  name: string;
  rows: number;
};

export type DbCleanupPreview = {
  cutoff: string;
  cutoff_game_id: number;
  tables: DbCleanupTableStat[];
  total_rows: number;
};

export type DbCleanupTableProgress = {
  name: string;
  total: number;
  deleted: number;
  status: 'pending' | 'running' | 'done';
};

export type DbCleanupProgress = {
  task_id: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  cutoff: string;
  cutoff_game_id: number;
  tables: DbCleanupTableProgress[];
  started_at: string;
  updated_at: string;
  error?: string | null;
};

/** 预览 6 个月前待清理的数据量（不写库） */
export async function getDbCleanupPreview(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: DbCleanupPreview;
  }>('/api/admin/maintenance/db-cleanup/preview', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 启动异步清理任务 */
export async function startDbCleanup(
  body: { confirm: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: { task_id: string };
  }>('/api/admin/maintenance/db-cleanup/execute', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 查询当前清理任务进度 */
export async function getDbCleanupProgress(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: DbCleanupProgress | null;
  }>('/api/admin/maintenance/db-cleanup/progress', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 请求取消当前清理任务（当前批次完成后停止） */
export async function cancelDbCleanup(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
  }>('/api/admin/maintenance/db-cleanup/cancel', {
    method: 'POST',
    ...(options || {}),
  });
}

// ========== 系统维护 - 赛季切换 ==========

export type SeasonSwitchNodeStatus = 'pending' | 'done';

/** 赛季切换计划概要（列表/详情共用） */
export interface SeasonSwitchPlanSummary {
  id: number;
  name: string;
  /** 计划停服/切换时刻，UTC 毫秒；null 表示未设置 */
  switch_at_ms?: number | null;
  /** 计划关联的飞书文档链接 */
  doc_url?: string | null;
  created_by: string;
  created_at: string;
  total_nodes: number;
  done_nodes: number;
  /** 已过计划时间但仍未完成的节点数（由后端按服务端时间计算） */
  overdue_nodes: number;
}

export interface SeasonSwitchNode {
  id: number;
  seq: number;
  node_key: string;
  title: string;
  description: string;
  /** 负责人展示名，多人用顿号分隔 */
  owner: string;
  /** 计划时间，UTC 毫秒；null 表示待定 */
  planned_at_ms?: number | null;
  status: SeasonSwitchNodeStatus;
  done_at_ms?: number | null;
  done_by?: string | null;
  note?: string | null;
  updated_by: string;
  updated_at?: string | null;
}

export interface SeasonSwitchPlanDetail {
  plan: SeasonSwitchPlanSummary;
  nodes: SeasonSwitchNode[];
  /** 服务端当前时刻（UTC 毫秒），前端用于对齐倒计时 */
  server_now_ms: number;
}

export interface SeasonSwitchLog {
  id: number;
  node_id?: number | null;
  node_title: string;
  /** create_plan / update_plan_* / add_node / delete_node / update_* / complete / reopen */
  action: string;
  old_value?: string | null;
  new_value?: string | null;
  note?: string | null;
  operator: string;
  created_at: string;
}

/** 计划列表；首次打开时后端会自动建出 S6 默认计划 */
export async function listSeasonSwitchPlans(options?: { [key: string]: any }) {
  return request<{
    success: boolean;
    message: string;
    data?: { plans: SeasonSwitchPlanSummary[]; server_now_ms: number };
  }>('/api/admin/maintenance/season-switch/plans', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新建计划；copy_from_plan_id 为空时使用内置 S6 节点模板 */
export async function createSeasonSwitchPlan(
  body: {
    name: string;
    switch_at_ms?: number | null;
    copy_from_plan_id?: number | null;
    /** 复制时按新旧停服时间差整体平移节点时间 */
    shift_node_times?: boolean;
    doc_url?: string | null;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchPlanDetail;
  }>('/api/admin/maintenance/season-switch/plans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 修改计划名称 / 停服时间 / 关联文档。doc_url 传空字符串表示解除关联 */
export async function updateSeasonSwitchPlan(
  planId: number,
  body: {
    name?: string;
    switch_at_ms?: number;
    clear_switch_at?: boolean;
    doc_url?: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchPlanDetail;
  }>(`/api/admin/maintenance/season-switch/plans/${planId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 计划详情（含全部节点） */
export async function getSeasonSwitchPlan(
  planId: number,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchPlanDetail;
  }>(`/api/admin/maintenance/season-switch/plans/${planId}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 修改节点标题 / 说明 / 计划时间 / 负责人 / 备注。clear_planned_at=true 时清空计划时间 */
export async function updateSeasonSwitchNode(
  nodeId: number,
  body: {
    title?: string;
    description?: string;
    planned_at_ms?: number;
    clear_planned_at?: boolean;
    owner?: string;
    note?: string;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchNode;
  }>(`/api/admin/maintenance/season-switch/nodes/${nodeId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 新增节点；insert_after_seq=0 或不传表示追加到末尾 */
export async function createSeasonSwitchNode(
  planId: number,
  body: {
    title: string;
    description?: string;
    owner?: string;
    planned_at_ms?: number;
    insert_after_seq?: number;
  },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchPlanDetail;
  }>(`/api/admin/maintenance/season-switch/plans/${planId}/nodes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 删除节点（操作记录保留节点标题快照） */
export async function deleteSeasonSwitchNode(
  nodeId: number,
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchPlanDetail;
  }>(`/api/admin/maintenance/season-switch/nodes/${nodeId}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 标记节点完成；完成后记录当前登录人与服务端时间 */
export async function completeSeasonSwitchNode(
  nodeId: number,
  body: { note?: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchNode;
  }>(`/api/admin/maintenance/season-switch/nodes/${nodeId}/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 撤销完成，把节点重置为待执行 */
export async function reopenSeasonSwitchNode(
  nodeId: number,
  body: { note?: string },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchNode;
  }>(`/api/admin/maintenance/season-switch/nodes/${nodeId}/reopen`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: body,
    ...(options || {}),
  });
}

/** 计划级操作记录（时间/负责人/完成状态的变更） */
export async function getSeasonSwitchLogs(
  planId: number,
  params?: { limit?: number },
  options?: { [key: string]: any },
) {
  return request<{
    success: boolean;
    message: string;
    data?: SeasonSwitchLog[];
  }>(`/api/admin/maintenance/season-switch/plans/${planId}/logs`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}
